function createGuideTracker(root) {
  const params = new URL(location.href).searchParams;
  const isTest = () => params.has('webPixelDebug') || !!document.getElementById('web-pixels-helper-sandbox-iframe');
  const preview = root.dataset.designMode === 'true' || window.Shopify?.theme?.role === 'unpublished' || params.has('preview_theme_id');
  let opened = false;
  return (name, details = {}) => {
    try {
      const testMode = isTest();
      if ((preview && !testMode) || root.dataset.designMode === 'true' || typeof window.Shopify?.analytics?.publish !== 'function') return;
      if (!['opened', 'step_viewed', 'search_completed', 'product_opened', 'contact_opened', 'browse_opened'].includes(name)) return;
      if (name === 'opened' && opened) return;
      const data = { guide_version: 1, test_mode: testMode };
      if (Number.isInteger(details.step) && details.step >= 1 && details.step <= 4) data.guide_step = details.step;
      if (Number.isInteger(details.count) && details.count >= 0) data.result_count = Math.min(details.count, 100000);
      const pending = window.Shopify.analytics.publish('bdt_guide:' + name, data);
      if (name === 'opened') opened = true;
      Promise.resolve(pending).catch(() => {});
    } catch {}
  };
}

for (const root of document.querySelectorAll('[data-bdt-guide]')) {
  if (root.dataset.ready) continue;
  root.dataset.ready = 'true';
  const messages = JSON.parse(root.querySelector('[data-guide-messages]').textContent);
  const text = (key, values = {}) => messages[key].replace(/__([a-z_]+)__|{{\s*([a-z_]+)\s*}}/g, (_, a, b) => values[a || b] ?? '');
  const money = value => new Intl.NumberFormat(root.dataset.locale, { style: 'currency', currency: root.dataset.currency }).format(value);
  const number = value => new Intl.NumberFormat(root.dataset.locale, { maximumFractionDigits: 3 }).format(value);
  const form = root.querySelector('[data-guide-form]');
  const panel = root.querySelector('[data-panel]');
  const start = root.querySelector('[data-start]');
  const screens = [...root.querySelectorAll('[data-screen]')];
  const steps = [...root.querySelectorAll('[data-step]')];
  const next = root.querySelector('[data-next]');
  const back = root.querySelector('[data-back]');
  const results = root.querySelector('[data-results]');
  const status = root.querySelector('[data-status]');
  const error = root.querySelector('[data-error]');
  const dimensionsAny = root.querySelector('[data-dimensions-any]');
  const dimensionsEdit = root.querySelector('[data-dimensions-edit]');
  const dimensionFields = root.querySelector('[data-dimension-fields]');
  const dimensionDefault = root.querySelector('[data-dimensions-default]');
  const characterQuestion = root.querySelector('[data-character-question]');
  const dimensionInputs = [...form.querySelectorAll('input[name="length"], input[name="width"], input[name="thickness"]')];
  const thicknessInput = form.querySelector('input[name="thickness"]');
  const thicknessError = root.querySelector('[data-thickness-error]');
  let dimensionsExpanded = false;
  const track = createGuideTracker(root);
  let screen = 0;
  let visited = 0;
  let busy = false;
  let core;
  let scrollRevision = 0;
  function focusGuide(target, anchor = target) {
    const revision = ++scrollRevision;
    target.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (revision !== scrollRevision || panel.hidden) return;
      anchor.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
  }
  const preservePreview = url => {
    const destination = new URL(url, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) destination.searchParams.set('preview_theme_id', preview);
    return destination;
  };
  root.querySelectorAll('[data-browse]').forEach(a => { a.href = preservePreview(a.href).href; });
  function showStep(index, focus = true) {
    screen = index;
    visited = Math.max(visited, index);
    form.hidden = false;
    results.hidden = true;
    error.hidden = true;
    status.textContent = '';
    screens.forEach((s, i) => { s.hidden = i !== index; });
    steps.forEach((button, i) => {
      button.disabled = i > visited;
      if (i === index) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    back.hidden = index === 0;
    next.hidden = false;
    if (index === 1) syncDimensions();
    next.textContent = text(index === 3 ? 'submit' : 'next') + ' →';
    if (focus) focusGuide(screens[index].querySelector('legend'), panel);
    if (!panel.hidden) track('step_viewed', { step: index + 1 });
  }
  function close() {
    scrollRevision++;
    panel.hidden = true;
    start.setAttribute('aria-expanded', 'false');
    start.focus();
  }
  start.addEventListener('click', () => {
    panel.hidden = false;
    track('opened');
    start.setAttribute('aria-expanded', 'true');
    if (results.hidden) showStep(screen);
    else focusGuide(results.querySelector('h2'));
  });
  root.querySelector('[data-close]').addEventListener('click', close);
  root.querySelectorAll('[data-browse]').forEach(a => a.addEventListener('click', () => { track('browse_opened'); panel.hidden = true; start.setAttribute('aria-expanded', 'false'); }));
  panel.addEventListener('keydown', event => { if (event.key === 'Escape') close(); });
  steps.forEach((button, i) => button.addEventListener('click', () => { if (!busy) showStep(i); }));
  back.addEventListener('click', () => showStep(screen - 1));
  form.querySelectorAll('input[name="project"]').forEach(input => input.addEventListener('click', () => {
    if (!busy && screen === 0 && input.checked) showStep(1);
  }));
  const hasDimensions = () => dimensionInputs.some(input => input.value !== '' || input.validity.badInput);
  const checked = name => form.querySelector('input[name="' + name + '"]:checked');
  function syncDimensions() {
    const specified = hasDimensions();
    dimensionsAny.setAttribute('aria-pressed', String(!specified));
    dimensionsEdit.setAttribute('aria-pressed', String(specified));
    dimensionsEdit.setAttribute('aria-expanded', String(dimensionsExpanded));
    dimensionFields.hidden = !dimensionsExpanded;
    dimensionDefault.hidden = specified;
  }
  function checkThickness() {
    const invalid = thicknessInput.validity.rangeOverflow;
    thicknessInput.setCustomValidity(invalid ? text('thickness_limit') : '');
    thicknessInput.setAttribute('aria-invalid', String(invalid));
    thicknessError.textContent = invalid ? text('thickness_limit') : '';
    thicknessError.hidden = !invalid;
  }
  function validDimensions() {
    checkThickness();
    return validScreen(1);
  }
  dimensionsAny.addEventListener('click', () => {
    if (busy) return;
    dimensionInputs.forEach(input => { input.value = ''; });
    dimensionsExpanded = false;
    checkThickness();
    syncDimensions();
  });
  dimensionsEdit.addEventListener('click', () => {
    if (busy) return;
    dimensionsExpanded = true;
    syncDimensions();
    focusGuide(dimensionFields.querySelector('h3'));
  });
  dimensionInputs.forEach(input => input.addEventListener('input', () => {
    checkThickness();
    syncDimensions();
  }));
  form.querySelectorAll('input[name="edge"]').forEach(input => input.addEventListener('click', () => {
    if (busy || screen !== 2 || !input.checked) return;
    characterQuestion.hidden = false;
    focusGuide(characterQuestion.querySelector('h3'));
  }));
  form.querySelectorAll('input[name="character"]').forEach(input => input.addEventListener('click', () => {
    if (!busy && screen === 2 && input.checked) showStep(3);
  }));
  function criteria() {
    const values = new FormData(form);
    const thicknessCm = core.numeric(values.get('thickness'));
    return { project: values.get('project'), length: core.numeric(values.get('length')), width: core.numeric(values.get('width')), thickness: thicknessCm == null ? null : thicknessCm * 10, strict: false, machining: true, edge: values.get('edge'), character: values.get('character'), budget: core.numeric(values.get('budget')), tolerance: { length: null, width: null, thickness: null } };
  }
  function validScreen(index = screen) {
    for (const input of screens[index].querySelectorAll('input')) {
      if (!input.checkValidity()) {
        input.closest('details')?.setAttribute('open', '');
        input.reportValidity();
        return false;
      }
    }
    return true;
  }
  async function catalog() {
    const products = new Map();
    const visitedPages = new Set();
    let url = preservePreview(root.dataset.catalogUrl);
    url.searchParams.set('section_id', 'bdt-guide-catalog');
    let expected;
    while (url) {
      if (visitedPages.has(url.href)) throw new Error('Repeated catalogue page');
      visitedPages.add(url.href);
      const response = await fetch(url, { signal: AbortSignal.timeout(20000), cache: 'no-store' });
      if (!response.ok) throw new Error('Catalogue unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = doc.querySelector('[data-bdt-catalog]');
      if (!payload) throw new Error('Catalogue missing');
      const data = JSON.parse(payload.textContent);
      expected ??= data.total;
      if (!Number.isInteger(data.total) || data.total !== expected || !Array.isArray(data.products)) throw new Error('Catalogue changed');
      data.products.forEach(p => products.set(p.id, p));
      url = data.next ? preservePreview(data.next) : null;
      if (url) {
        if (url.origin !== location.origin || url.pathname !== new URL(root.dataset.catalogUrl, location.origin).pathname) throw new Error('Invalid catalogue URL');
        url.searchParams.set('section_id', 'bdt-guide-catalog');
      }
    }
    if (products.size !== expected) throw new Error('Incomplete catalogue');
    return [...products.values()];
  }
  function element(tag, content, className) {
    const node = document.createElement(tag);
    if (content != null) node.textContent = content;
    if (className) node.className = className;
    return node;
  }
  function target(c) {
    return text('target', { length: c.length == null ? text('unset') : number(c.length), width: c.width == null ? text('unset') : number(c.width), thickness: c.thickness == null ? text('unset') : number(c.thickness / 10) });
  }
  function dimensions(board) {
    const width = (board.approxWidth ? text('approx_prefix') + ' ' : '') + number(board.widthMinCm) + (board.widthMaxCm === board.widthMinCm ? '' : '–' + number(board.widthMaxCm));
    return text('dimensions_line', { length: number(board.lengthCm), width, thickness: number(board.thicknessMm / 10) });
  }
  function signed(value) { return (value > 0 ? '+' : value < 0 ? '−' : '') + number(Math.abs(value)); }
  function deviations(delta) {
    return ['length', 'width', 'thickness'].filter(axis => delta[axis]).map(axis => {
      const divisor = axis === 'thickness' ? 10 : 1;
      const [min, max] = delta[axis].map(value => value / divisor);
      if (Math.abs(min) < 0.000001 && Math.abs(max) < 0.000001) return text('unchanged', { axis: text(axis) });
      return text('delta', { axis: text(axis), amount: signed(min) + (Math.abs(max - min) > 0.000001 ? ' à ' + signed(max) : ''), unit: 'cm' });
    }).join(' · ');
  }
  function request(c, item) {
    const projectNames = { any: 'any_project', etagere: 'etagere', tablette: 'tablette', plateau: 'plateau', bar: 'bar' };
    const edgeNames = { any: 'any_style', natural: 'natural', straight: 'straight' };
    const lines = [text('request_intro'), text('request_project', { value: text(projectNames[c.project]) }), target(c), text('request_edge', { value: text(edgeNames[c.edge]) }), text('request_character', { value: text(c.character === 'any' ? 'any_style' : c.character) }), text('request_budget', { value: c.budget == null ? text('unset') : money(c.budget) })];
    if (item) {
      lines.push(text('request_piece', { value: item.product.title }), new URL(item.product.url, location.origin).href);
      item.product.guide.boards.forEach((board, i) => lines.push(text('board', { count: i + 1 }) + ' : ' + dimensions(board)));
      lines.push(text(item.canReduce ? 'request_machining' : 'request_dimensions'));
    } else lines.push(text('request_custom'));
    const url = preservePreview(root.dataset.contactUrl);
    url.searchParams.set('bdt_request', lines.join('\n'));
    url.hash = 'ContactForm';
    return url.href;
  }
  function card(item, c) {
    const p = item.product;
    const article = element('article', null, 'bdt-guide__card');
    const imageLink = element('a', null, 'bdt-guide__photo');
    imageLink.href = preservePreview(p.url).href;
    imageLink.setAttribute('tabindex', '-1');
    imageLink.setAttribute('aria-hidden', 'true');
    if (p.image) {
      const image = element('img');
      image.src = p.image;
      image.alt = '';
      image.width = 700;
      image.height = 700;
      image.loading = 'lazy';
      imageLink.append(image);
    }
    article.append(imageLink);
    const body = element('div', null, 'bdt-guide__card-body');
    const title = element('h4', null, 'bdt-guide__card-title');
    const titleLink = element('a', p.title);
    titleLink.href = imageLink.href;
    imageLink.addEventListener('click', () => track('product_opened'));
    titleLink.addEventListener('click', () => track('product_opened'));
    title.append(titleLink);
    body.append(title);
    const badges = element('div', null, 'bdt-guide__badges');
    const badge = (key, suffix, values) => badges.append(element('span', text(key, values), 'bdt-guide__badge' + (suffix ? ' bdt-guide__badge--' + suffix : '')));
    if (item.canReduce) badge('machine_badge', 'machine');
    if (item.exact) badge('same_dimensions');
    if (item.approx) badge('approx_badge');
    if (item.styleMismatch && !item.styleUnknown) badge('style_badge');
    if (item.group === 'unknown') badge('unknown_badge');
    if (item.overBudget) badge('over_budget', 'budget', { amount: money(item.overBudget) });
    body.append(badges);
    if (core.validGuide(p.guide)) {
      if (p.guide.boards.length > 1) body.append(element('p', text('lot', { count: p.guide.boards.length })));
      p.guide.boards.forEach((board, i) => {
        const block = element('div');
        block.append(element('p', (p.guide.boards.length > 1 ? text('board', { count: i + 1 }) + ' : ' : '') + dimensions(board)));
        if (item.deltas[i] && !item.exact) block.append(element('p', deviations(item.deltas[i])));
        body.append(block);
      });
    }
    if (item.canReduce) body.append(element('p', target(c)), element('p', text('machine_note')));
    body.append(element('p', text('from', { amount: money(p.price / 100) }), 'bdt-guide__price'));
    const view = element('a', text('view'), 'bdt-guide__button');
    view.href = imageLink.href;
    view.addEventListener('click', () => track('product_opened'));
    body.append(view);
    if (item.canReduce) {
      const action = element('a', text('machine_request'), 'bdt-guide__button bdt-guide__button--primary');
      action.href = request(c, item);
      action.addEventListener('click', () => track('contact_opened'));
      body.append(action);
    }
    article.append(body);
    return article;
  }
  function render(items, c) {
    results.replaceChildren();
    const heading = element('h2', text('results_title'));
    heading.tabIndex = -1;
    results.append(heading, element('p', target(c), 'bdt-guide__hint'), element('p', text('price_note'), 'bdt-guide__hint'), element('p', text('nominal_note'), 'bdt-guide__hint'));
    const edit = element('button', text('edit'), 'bdt-guide__button');
    edit.type = 'button';
    edit.addEventListener('click', () => showStep(0));
    results.append(edit);
    if (!items.length) results.append(element('p', text('no_match'), 'bdt-guide__hint'));
    for (const group of ['exact', 'machining', 'near', 'other', 'unknown']) {
      const subset = items.filter(item => item.group === group);
      if (!subset.length) continue;
      const groupNode = element('section', null, 'bdt-guide__group');
      const groupKey = group === 'near' && Object.values(c.tolerance).every(v => v == null) ? 'other_sizes' : group === 'machining' ? 'machining_group' : group;
      groupNode.append(element('h3', text(groupKey)));
      const cards = element('div', null, 'bdt-guide__cards');
      let shown = 0;
      const more = element('button', null, 'bdt-guide__button bdt-guide__more');
      more.type = 'button';
      function add() {
        const end = shown === 0 ? Math.min(3, subset.length) : subset.length;
        for (; shown < end; shown++) cards.append(card(subset[shown], c));
        more.hidden = shown === subset.length;
        more.textContent = text(subset.length - shown === 1 ? 'more_one' : 'more', { count: subset.length - shown });
        status.textContent = text(items.length === 1 ? 'selection_one' : 'selection_count', { count: items.length });
      }
      more.addEventListener('click', add);
      add();
      groupNode.append(cards, more);
      results.append(groupNode);
    }
    const custom = element('div', null, 'bdt-guide__request');
    const words = element('div');
    words.append(element('h3', text('request_title')), element('p', text('request_hint')));
    const action = element('a', text('request'), 'bdt-guide__button bdt-guide__button--primary');
    action.href = request(c);
    action.addEventListener('click', () => track('contact_opened'));
    custom.append(words, action);
    results.append(custom);
    form.hidden = true;
    steps.forEach(button => button.removeAttribute('aria-current'));
    results.hidden = false;
    focusGuide(heading);
  }
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy) return;
    if (screen === 1) {
      if (!validDimensions()) return;
      return showStep(2);
    }
    if (screen === 2) {
      if (characterQuestion.hidden) {
        characterQuestion.hidden = false;
        return focusGuide(characterQuestion.querySelector('h3'));
      }
      return showStep(3);
    }
    if (!validScreen()) return;
    if (screen === 0) return showStep(1);
    const dimensionsInvalid = dimensionInputs.some(input => !input.checkValidity());
    if (dimensionsInvalid) {
      dimensionsExpanded = true;
      showStep(1);
      validDimensions();
      return;
    }
    if (!checked('edge') || !checked('character')) return showStep(2);
    busy = true;
    next.disabled = true;
    back.disabled = true;
    error.hidden = true;
    status.textContent = text('loading');
    try {
      core ??= await import(root.dataset.coreUrl);
      const c = criteria();
      const items = core.recommend(await catalog(), c);
      render(items, c);
      track('search_completed', { count: items.length });
      status.textContent = text(items.length === 1 ? 'selection_one' : 'selection_count', { count: items.length });
    } catch {
      error.textContent = text('load_error');
      error.hidden = false;
      status.textContent = '';
      next.textContent = text('retry');
    } finally {
      busy = false;
      next.disabled = false;
      back.disabled = false;
    }
  });
  showStep(0, false);
  start.hidden = false;
}
